import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import articles from "./articlesData";
import Seo from "./Seo";

function estimateReadTimeFromText(text) {
  if (!text) return 1;
  const words = text.trim().split(/\s+/).length;
  return Math.max(1, Math.ceil(words / 200));
}

const categoryIcons = {
  PROGRAMMING: (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 18 22 12 16 6"/>
      <polyline points="8 6 2 12 8 18"/>
    </svg>
  ),
  "CYBER SECURITY": (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  ),
  NETWORKING: (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <line x1="2" y1="12" x2="22" y2="12"/>
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
    </svg>
  ),
  AWS: (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/>
    </svg>
  ),
};

const categoryColors = {
  PROGRAMMING: { bg: "rgba(168, 85, 247, 0.1)", text: "#9333ea", border: "rgba(168, 85, 247, 0.3)" },
  "CYBER SECURITY": { bg: "rgba(239, 68, 68, 0.1)", text: "#dc2626", border: "rgba(239, 68, 68, 0.3)" },
  NETWORKING: { bg: "rgba(59, 130, 246, 0.1)", text: "#2563eb", border: "rgba(59, 130, 246, 0.3)" },
  AWS: { bg: "rgba(251, 146, 60, 0.1)", text: "#ea580c", border: "rgba(251, 146, 60, 0.3)" },
};

export default function LearnGrid() {
  const [reads, setReads] = useState({});
  const [filter, setFilter] = useState("All");

  const categories = ["All", ...new Set(articles.map((a) => a.category))];
  const filteredArticles = filter === "All" 
    ? articles 
    : articles.filter((a) => a.category === filter);

  useEffect(() => {
    let isMounted = true;
    async function load() {
      const entries = await Promise.all(
        articles.map(async (a) => {
          try {
            const res = await fetch(a.file);
            const txt = await res.text();
            return [a.id, estimateReadTimeFromText(txt)];
          } catch (e) {
            return [a.id, 5];
          }
        })
      );
      if (isMounted) {
        const map = Object.fromEntries(entries);
        setReads(map);
      }
    }
    load();
    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div className="learn-container">
      <Seo
        title="Technical Notes - LinuxSecOps"
        description="Browse technical guides and tutorials on security, programming, networking and AWS."
        path="/learn"
      />

      {/* Hero Section */}
      <section className="learn-hero">
        <div className="learn-hero-content">
          <div className="hero-badge">Notes</div>
          <h1 className="learn-hero-title">
            Technical Notes
            <span className="learn-hero-accent"> & Guides</span>
          </h1>
          <p className="learn-hero-description">
            Practical guides, tutorials, and documentation covering security,
            programming, networking, and cloud infrastructure.
          </p>
          <div className="learn-hero-stats">
            <div className="learn-stat">
              <span className="learn-stat-value">{articles.length}</span>
              <span className="learn-stat-label">Guides</span>
            </div>
            <div className="learn-stat-divider"></div>
            <div className="learn-stat">
              <span className="learn-stat-value">{categories.length - 1}</span>
              <span className="learn-stat-label">Categories</span>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="learn-content">
        {/* Filter */}
        <div className="learn-filter">
          {categories.map((cat) => (
            <button
              key={cat}
              className={`learn-filter-btn ${filter === cat ? "active" : ""}`}
              onClick={() => setFilter(cat)}
            >
              {cat === "All" ? "All" : cat}
              {cat !== "All" && (
                <span className="learn-filter-count">
                  {articles.filter((a) => a.category === cat).length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="learn-grid">
          {filteredArticles.map((a) => (
            <Link key={a.id} to={a.path} className="learn-card">
              <div className="learn-card-header">
                <div
                  className="learn-card-icon"
                  style={{ 
                    background: categoryColors[a.category]?.bg || "#f1f5f9",
                    color: categoryColors[a.category]?.text || "#475569"
                  }}
                >
                  {categoryIcons[a.category]}
                </div>
                <div className="learn-card-meta">
                  <span
                    className="learn-card-category"
                    style={{
                      background: categoryColors[a.category]?.bg || "#f1f5f9",
                      color: categoryColors[a.category]?.text || "#475569",
                      borderColor: categoryColors[a.category]?.border || "#e2e8f0",
                    }}
                  >
                    {a.category}
                  </span>
                </div>
              </div>
              <h3 className="learn-card-title">{a.title}</h3>
              <div className="learn-card-footer">
                <span className="learn-card-time">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                  </svg>
                  {reads[a.id] ? `${reads[a.id]} min read` : "..."}
                </span>
                <span className="learn-card-arrow">
                  Read
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12"/>
                    <polyline points="12 5 19 12 12 19"/>
                  </svg>
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
